import React, { useState, useRef, useCallback } from 'react';

/**
 * PhotoCapture component - Capture or upload player photo with background removal.
 * Uses @imgly/background-removal for client-side AI background removal.
 *
 * Props:
 * - currentPhotoUrl: string | null - Current photo URL
 * - onPhotoReady: (blob: Blob) => Promise<void> - Called with the processed photo blob
 * - playerName: string - Player name for display
 */
const PhotoCapture = ({ currentPhotoUrl, onPhotoReady, playerName }) => {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState('select'); // select, preview, processing, result
  const [originalImage, setOriginalImage] = useState(null); // data URL
  const [processedImage, setProcessedImage] = useState(null); // data URL
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const canvasRef = useRef(null);

  const resetState = useCallback(() => {
    setStep('select');
    setOriginalImage(null);
    setProcessedImage(null);
    setProcessing(false);
    setProgress(0);
    setProgressText('');
    setError('');
    setUploading(false);
  }, []);

  const openModal = () => {
    resetState();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetState();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('La imagen no debe superar los 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setOriginalImage(ev.target.result);
      setStep('preview');
      setError('');
    };
    reader.readAsDataURL(file);

    // Reset input
    e.target.value = '';
  };

  const processBackground = async () => {
    if (!originalImage) return;

    setProcessing(true);
    setStep('processing');
    setProgress(0);
    setProgressText('Cargando modelo de IA...');
    setError('');

    try {
      // Dynamic import to avoid loading the large model until needed
      const { removeBackground } = await import('@imgly/background-removal');

      setProgressText('Descargando modelo (primera vez ~40MB)...');
      setProgress(10);

      // Convert data URL to blob for the library
      const response = await fetch(originalImage);
      const imageBlob = await response.blob();

      setProgressText('Procesando imagen...');
      setProgress(30);

      // Remove background
      const resultBlob = await removeBackground(imageBlob, {
        progress: (key, current, total) => {
          if (total > 0) {
            const pct = Math.round(30 + (current / total) * 60);
            setProgress(Math.min(pct, 90));
          }
          if (key === 'compute:inference') {
            setProgressText('Removiendo fondo...');
          }
        },
      });

      setProgress(90);
      setProgressText('Aplicando fondo blanco...');

      // Draw on canvas with white background
      const img = new Image();
      const resultURL = URL.createObjectURL(resultBlob);

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = resultURL;
      });

      const canvas = canvasRef.current;
      // Target: 800x800 square, scale image to cover
      const maxSize = Math.min(Math.max(img.width, img.height), 800);
      canvas.width = maxSize;
      canvas.height = maxSize;
      const ctx = canvas.getContext('2d');

      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, maxSize, maxSize);

      // Scale to cover the square, then center-crop
      const scale = Math.max(maxSize / img.width, maxSize / img.height);
      const scaledW = img.width * scale;
      const scaledH = img.height * scale;
      const offsetX = (maxSize - scaledW) / 2;
      const offsetY = (maxSize - scaledH) / 2;
      ctx.drawImage(img, offsetX, offsetY, scaledW, scaledH);

      URL.revokeObjectURL(resultURL);

      const processedDataURL = canvas.toDataURL('image/jpeg', 0.9);
      setProcessedImage(processedDataURL);
      setStep('result');
      setProgress(100);
      setProgressText('Listo!');
    } catch (err) {
      console.error('Background removal error:', err);
      setError('Error al procesar la imagen. Intente con otra foto.');
      setStep('preview');
    } finally {
      setProcessing(false);
    }
  };

  const skipBackgroundRemoval = async () => {
    // Use original image as-is, just resize to square
    if (!originalImage) return;

    setProcessing(true);
    setStep('processing');
    setProgressText('Redimensionando...');

    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = originalImage;
      });

      const canvas = canvasRef.current;
      const maxSize = Math.min(Math.max(img.width, img.height), 800);
      canvas.width = maxSize;
      canvas.height = maxSize;
      const ctx = canvas.getContext('2d');

      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, maxSize, maxSize);

      // Scale to cover the square, then center-crop
      const scale = Math.max(maxSize / img.width, maxSize / img.height);
      const scaledW = img.width * scale;
      const scaledH = img.height * scale;
      const offsetX = (maxSize - scaledW) / 2;
      const offsetY = (maxSize - scaledH) / 2;
      ctx.drawImage(img, offsetX, offsetY, scaledW, scaledH);

      const dataURL = canvas.toDataURL('image/jpeg', 0.9);
      setProcessedImage(dataURL);
      setStep('result');
    } catch (err) {
      setError('Error al procesar la imagen');
      setStep('preview');
    } finally {
      setProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!processedImage || !onPhotoReady) return;

    setUploading(true);
    setError('');

    try {
      // Convert data URL to blob
      const response = await fetch(processedImage);
      const blob = await response.blob();
      await onPhotoReady(blob);
      closeModal();
    } catch (err) {
      console.error('Error saving photo:', err);
      setError(err.response?.data?.error || 'Error al guardar la foto');
      setUploading(false);
    }
  };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={openModal}
        className="px-3 py-2 bg-lsfc-green-500 text-white rounded-lg hover:bg-lsfc-green-600 text-sm font-medium transition-all flex items-center gap-1.5"
      >
        <span>📸</span>
        <span>{currentPhotoUrl ? 'Cambiar Foto' : 'Tomar Foto'}</span>
      </button>

      {/* Hidden inputs */}
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />

      {/* Hidden canvas for processing */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <span>📸</span> Foto de {playerName}
              </h3>
              <button
                onClick={closeModal}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {error}
                </div>
              )}

              {/* Step 1: Select source */}
              {step === 'select' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 text-center">
                    Selecciona o toma una foto del jugador. El fondo se removerá automáticamente.
                  </p>

                  {/* Current photo preview */}
                  {currentPhotoUrl && (
                    <div className="flex justify-center">
                      <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-200">
                        <img src={currentPhotoUrl} alt="Actual" className="w-full h-full object-cover" />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => cameraInputRef.current?.click()}
                      className="flex flex-col items-center gap-2 p-6 bg-lsfc-blue-50 border-2 border-lsfc-blue-200 rounded-xl hover:bg-lsfc-blue-100 transition-all"
                    >
                      <span className="text-4xl">📷</span>
                      <span className="text-sm font-medium text-lsfc-blue-700">Tomar Foto</span>
                      <span className="text-xs text-gray-400">Usar cámara</span>
                    </button>

                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex flex-col items-center gap-2 p-6 bg-gray-50 border-2 border-gray-200 rounded-xl hover:bg-gray-100 transition-all"
                    >
                      <span className="text-4xl">📁</span>
                      <span className="text-sm font-medium text-gray-700">Subir Archivo</span>
                      <span className="text-xs text-gray-400">Desde galería</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Preview original and choose action */}
              {step === 'preview' && originalImage && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 text-center">Vista previa de la foto seleccionada</p>

                  <div className="flex justify-center">
                    <div className="w-48 h-48 rounded-xl overflow-hidden border-2 border-gray-200 shadow-md">
                      <img src={originalImage} alt="Original" className="w-full h-full object-cover" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={processBackground}
                      className="w-full py-3 bg-lsfc-green-500 text-white rounded-xl font-medium hover:bg-lsfc-green-600 transition-all flex items-center justify-center gap-2"
                    >
                      <span>✨</span> Remover Fondo (Recomendado)
                    </button>

                    <button
                      onClick={skipBackgroundRemoval}
                      className="w-full py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all text-sm"
                    >
                      Usar foto sin modificar
                    </button>

                    <button
                      onClick={() => { resetState(); }}
                      className="w-full py-2 text-gray-400 text-sm hover:text-gray-600"
                    >
                      ← Elegir otra foto
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Processing */}
              {step === 'processing' && (
                <div className="space-y-4 text-center py-6">
                  <div className="relative w-24 h-24 mx-auto">
                    <div className="animate-spin rounded-full h-24 w-24 border-4 border-gray-200 border-t-lsfc-green-500"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl">✨</span>
                    </div>
                  </div>

                  <p className="text-sm font-medium text-gray-700">{progressText}</p>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-lsfc-green-500 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-400">{progress}%</p>

                  {progress < 30 && (
                    <p className="text-xs text-amber-500">
                      La primera vez tarda más mientras descarga el modelo de IA (~40MB)
                    </p>
                  )}
                </div>
              )}

              {/* Step 4: Result - before/after comparison */}
              {step === 'result' && processedImage && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 text-center font-medium">Resultado</p>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center">
                      <p className="text-xs text-gray-400 mb-1">Original</p>
                      <div className="w-full aspect-square rounded-lg overflow-hidden border border-gray-200">
                        <img src={originalImage} alt="Original" className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-lsfc-green-600 mb-1 font-medium">Procesada</p>
                      <div className="w-full aspect-square rounded-lg overflow-hidden border-2 border-lsfc-green-300 shadow-md">
                        <img src={processedImage} alt="Procesada" className="w-full h-full object-cover" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={handleSave}
                      disabled={uploading}
                      className="w-full py-3 bg-lsfc-green-500 text-white rounded-xl font-medium hover:bg-lsfc-green-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                          Guardando...
                        </>
                      ) : (
                        <>
                          <span>💾</span> Guardar Foto
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => { resetState(); }}
                      disabled={uploading}
                      className="w-full py-2 text-gray-400 text-sm hover:text-gray-600 disabled:opacity-50"
                    >
                      ← Elegir otra foto
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoCapture;
